"use client";

import React, { useCallback, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { BottomSheet } from "../ui/bottom-sheet";
import { Product } from "@nusrif/types";
import { RegularTshirtSizeChart } from "./SizeChart/RegularTshirt";
import { BoxyFitTshirtSizeChart } from "./SizeChart/BoxyFitTshirtSizeChart";
import { OversizeFitTshirtSizeChart } from "./SizeChart/OversizeFitTshirtSizeChart";

interface SizeSelectionBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  selectedSize: string;
  onSizeSelect: (size: string) => void;
  onAddToCart: () => void;
  isAddingToCart?: boolean;
}

export const SizeSelectionBottomSheet: React.FC<
  SizeSelectionBottomSheetProps
> = ({
  isOpen,
  onClose,
  product,
  selectedSize,
  onSizeSelect,
  onAddToCart,
  isAddingToCart,
}) => {
  // Size guide bottom sheet state
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);

  // Get stock status for each size option
  const sizeStockStatus = useMemo(() => {
    return product.sizes.map((size) => {
      const variant = product.variants.find((v) =>
        v.options.some((opt) => opt.name === size)
      );

      if (!variant) {
        return {
          size,
          isInStock: false,
          stockLevel: 0,
          stockStatus: "out_of_stock" as const,
        };
      }

      const stockLevel = variant.stockLevels.reduce(
        (sum, level) => sum + level.stockOnHand - level.stockAllocated,
        0
      );
      const isInStock = stockLevel > variant.outOfStockThreshold;

      let stockStatusType: "in_stock" | "low_stock" | "out_of_stock";
      if (stockLevel <= variant.outOfStockThreshold) {
        stockStatusType = "out_of_stock";
      } else if (stockLevel <= 5) {
        stockStatusType = "low_stock";
      } else {
        stockStatusType = "in_stock";
      }

      return { size, isInStock, stockLevel, stockStatus: stockStatusType };
    });
  }, [product]);

  const handleSizeSelect = useCallback(
    (size: string) => {
      onSizeSelect(size);
    },
    [onSizeSelect]
  );

  const handleSizeGuideClick = useCallback(() => {
    // Implement size guide logic here, e.g., open a modal or navigate to a page

    setIsSizeGuideOpen(true);
  }, []);

  const handleAddToCart = useCallback(() => {
    if (selectedSize) {
      onAddToCart();
      onClose();
    }
  }, [selectedSize, onAddToCart, onClose]);

  const getSizeButtonVariant = (
    stockStatus: "in_stock" | "low_stock" | "out_of_stock",
    isSelected: boolean
  ) => {
    if (!isSelected) {
      switch (stockStatus) {
        case "out_of_stock":
          return "outline";
        case "low_stock":
          return "outline";
        case "in_stock":
          return "outline";
      }
    }
    return isSelected ? "default" : "outline";
  };

  const getSizeButtonClassName = (
    stockStatus: "in_stock" | "low_stock" | "out_of_stock",
    isSelected: boolean
  ) => {
    const baseClasses =
      "w-12 h-12 rounded-full text-sm font-medium transition-all duration-200";

    if (isSelected) {
      return baseClasses;
    }

    switch (stockStatus) {
      case "out_of_stock":
        return `${baseClasses} border-gray-200 text-gray-400 cursor-not-allowed opacity-50`;
      case "low_stock":
        return `${baseClasses} border-orange-300 text-orange-700 hover:border-orange-400 hover:bg-orange-50`;
      case "in_stock":
        return `${baseClasses} border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50`;
    }
  };

  const getStockIndicator = (
    stockStatus: "in_stock" | "low_stock" | "out_of_stock",
    stockLevel: number
  ) => {
    switch (stockStatus) {
      case "out_of_stock":
        return <span className="text-xs text-gray-400">Out of stock</span>;
      case "low_stock":
        return (
          <span className="text-xs text-orange-600">
            Only {stockLevel} left
          </span>
        );
      case "in_stock":
        return null;
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Select Size">
      <div className="space-y-6">
        {/* Product Info */}
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
          <img
            src={product.featuredAsset?.preview || product.images[0]}
            alt={product.name}
            className="w-16 h-16 rounded-lg object-cover"
          />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">{product.name}</h4>
            <p className="text-sm text-gray-600">Choose your size</p>
          </div>
        </div>

        {/* Size Selection */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Available Sizes</h4>
          <div className="flex flex-wrap gap-3">
            {sizeStockStatus.map(({ size, stockLevel, stockStatus }) => (
              <div key={size} className="flex flex-col items-center gap-1">
                <Button
                  variant={getSizeButtonVariant(
                    stockStatus,
                    selectedSize === size
                  )}
                  className={getSizeButtonClassName(
                    stockStatus,
                    selectedSize === size
                  )}
                  onClick={() => handleSizeSelect(size)}
                  disabled={stockStatus === "out_of_stock"}
                >
                  {size}
                </Button>
                {getStockIndicator(stockStatus, stockLevel)}
              </div>
            ))}
          </div>
        </div>

        {/* Size Guide */}
        <div className="pt-2 border-t border-gray-100">
          <button
            className="text-sm text-primary-blue hover:underline"
            onClick={() => handleSizeGuideClick()}
          >
            Size Guide
          </button>
        </div>

        {/* Add to Cart Button */}
        <div className="pt-4">
          <button
            onClick={handleAddToCart}
            disabled={!selectedSize}
            className={`w-full px-8 py-4 rounded-full font-semibold text-lg transition-colors duration-200 flex items-center justify-center ${
              selectedSize
                ? "bg-primary-blue text-white hover:bg-primary-blue/90"
                : "bg-primary-blue text-white hover:bg-primary-blue/90"
            }`}
          >
            {isAddingToCart
              ? "Adding..."
              : selectedSize
                ? `Add to Cart - ${selectedSize}`
                : "Select Size"}
          </button>
        </div>
      </div>

      <BottomSheet
        title="Size Guide"
        isOpen={isSizeGuideOpen}
        onClose={() => {
          setIsSizeGuideOpen(false);
        }}
      >
        {product.customFields.fit === "Regular Fit" && (
          <RegularTshirtSizeChart />
        )}
        {product.customFields.fit === "Boxy Fit with dropped shoulders" && (
          <BoxyFitTshirtSizeChart />
        )}
        {product.customFields.fit === "Oversize" && (
          <OversizeFitTshirtSizeChart />
        )}
      </BottomSheet>
    </BottomSheet>
  );
};
